/**
 * 값의 키를 스키마의 `properties` 순서로 다시 놓는다. 사람이 읽는 JSON 을 늘 같은 순서로 쓰려는 것이다.
 * 편집 헬퍼는 키를 자기 순서로 만든다. 그래서 같은 값도 편집 경로에 따라 순서가 다를 수 있다.
 *
 * 따르는 키워드는 `properties` · local `$ref` · `anyOf` · `items` · `prefixItems` 다. 스키마가 모르는 키는
 * 원래 순서로 뒤에 둔다. 값은 바꾸지 않는다. 키 순서만 바꾼 새 객체를 만든다.
 */
export function orderKeysBySchema(value: unknown, schema: object): unknown {
  return order(value, schema, schema);
}

type SchemaNode = Readonly<Record<string, unknown>>;

function order(value: unknown, node: unknown, root: object): unknown {
  const schema = resolve(node, root);
  if (Array.isArray(value)) {
    const branch = pickBranch(
      schema,
      root,
      (candidate) => "items" in candidate || "prefixItems" in candidate,
    );
    const prefix = Array.isArray(branch?.prefixItems) ? branch.prefixItems : [];
    return value.map((item, index) => order(item, prefix[index] ?? branch?.items, root));
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  const branch = pickBranch(schema, root, (candidate) => isRecord(candidate.properties));
  const properties = isRecord(branch?.properties) ? branch.properties : {};
  const source = value as Readonly<Record<string, unknown>>;
  const known = Object.keys(properties).filter((key) => hasOwn(source, key));
  const unknown = Object.keys(source).filter((key) => !hasOwn(properties, key));
  const result: Record<string, unknown> = {};
  for (const key of [...known, ...unknown]) {
    Object.defineProperty(result, key, {
      value: order(source[key], properties[key], root),
      enumerable: true,
      writable: true,
      configurable: true,
    });
  }
  return result;
}

/** 이 스키마, 또는 `anyOf` 가지 중 `fits` 를 만족하는 첫 스키마. 없으면 `null` 이다. */
function pickBranch(
  schema: SchemaNode | null,
  root: object,
  fits: (candidate: SchemaNode) => boolean,
): SchemaNode | null {
  if (schema === null) {
    return null;
  }
  if (fits(schema)) {
    return schema;
  }
  const branches = Array.isArray(schema.anyOf) ? schema.anyOf : [];
  for (const branch of branches) {
    const resolved = resolve(branch, root);
    if (resolved !== null && fits(resolved)) {
      return resolved;
    }
  }
  return null;
}

/** local `$ref`(`#/$defs/Hand`)를 따라간다. 다른 문서의 참조는 따라가지 않는다. */
function resolve(node: unknown, root: object): SchemaNode | null {
  let current = node;
  for (let depth = 0; depth < 32 && isRecord(current); depth += 1) {
    const ref = current.$ref;
    if (typeof ref !== "string" || !ref.startsWith("#/")) {
      return current;
    }
    current = ref
      .slice(2)
      .split("/")
      .reduce<unknown>((parent, segment) => (isRecord(parent) ? parent[segment] : undefined), root);
  }
  return null;
}

function hasOwn(target: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(target, key);
}

function isRecord(value: unknown): value is SchemaNode {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
